import React, { useEffect, useMemo, useState } from "react";
import CustomSpinner from "../../Components/CustomComponents/CustomSpinner";
import {
  useGetEngagementCampaignsQuery,
  useUpdateNotificationDetailMutation,
} from "../../Data/services/engagementApi";
import { ErrorToast, SuccessToast } from "../../Hooks/useToast";
import {
  extractData,
  extractLinks,
  hasNextPage,
  normalizeCampaigns,
} from "../Dashboard/utils";
import CampaignListHeader from "./components/CampaignListHeader";
import CampaignListPagination from "./components/CampaignListPagination";
import CampaignListTable from "./components/CampaignListTable";
import EditNotificationCopyModal from "./components/EditNotificationCopyModal";
import { ROUTES } from "./Route";
import styles from "./styles/campaignNotifications.module.scss";

const DEFAULT_PAGE_SIZE = 20;

export default function CampaignNotificationsScreen({
  setPage,
  setSelectedCampaign,
  setAudience,
}) {
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading, isFetching, isError, error } =
    useGetEngagementCampaignsQuery(
      { page: currentPage, limit: pageSize },
      { refetchOnMountOrArgChange: true }
    );
  const [updateNotificationDetail, { isLoading: isSaving }] =
    useUpdateNotificationDetailMutation();

  useEffect(() => {
    if (isError) {
      ErrorToast(error?.data?.message || "Failed to load campaigns");
    }
  }, [error, isError]);

  const campaigns = normalizeCampaigns(extractData(data));
  const links = extractLinks(data);
  const pageNumber = links.current || currentPage;
  const totalPages = links.total || 1;
  const totalRecords = links.total_records;
  const canPrev = pageNumber > 1;
  const canNext = hasNextPage(links, {
    loadedCount: campaigns.length,
    lastPageSize: campaigns.length,
    perPage: links.per_page || pageSize,
  });

  useEffect(() => {
    if (links.total > 0 && currentPage > links.total) {
      setCurrentPage(links.total);
    }
  }, [currentPage, links.total]);

  const categories = useMemo(() => {
    const unique = new Set();
    campaigns.forEach((campaign) => {
      const category =
        campaign?.notification_detail?.category || campaign?.trigger_type;
      if (category) unique.add(category);
    });
    return Array.from(unique).sort();
  }, [campaigns]);

  const visibleCampaigns = useMemo(() => {
    if (!categoryFilter) return campaigns;
    return campaigns.filter((campaign) => {
      const category =
        campaign?.notification_detail?.category || campaign?.trigger_type;
      return category === categoryFilter;
    });
  }, [campaigns, categoryFilter]);

  const handleCategoryChange = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openUsers = (campaign, audience) => {
    setSelectedCampaign(campaign);
    setAudience(audience);
    setPage(ROUTES.USERS);
  };

  const handleSaveCopy = async ({ title, message }) => {
    const detailsSlug = editingCampaign?.notification_detail?.slug;
    if (!detailsSlug) {
      ErrorToast("Notification details are missing for this campaign");
      return;
    }

    try {
      await updateNotificationDetail({
        detailsSlug,
        title,
        message,
      }).unwrap();
      SuccessToast("Notification copy updated");
      setEditingCampaign(null);
    } catch (err) {
      ErrorToast(err?.data?.message || "Failed to update notification copy");
    }
  };

  const initialLoad = isLoading && campaigns.length === 0;

  return (
    <div className={styles.MainContainer}>
      {editingCampaign ? (
        <EditNotificationCopyModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSave={handleSaveCopy}
          isSaving={isSaving}
        />
      ) : null}

      <CampaignListHeader
        categories={categories}
        categoryFilter={categoryFilter}
        onCategoryChange={handleCategoryChange}
      />

      <div className={styles.mainTable}>
        {initialLoad ? (
          <CustomSpinner />
        ) : (
          <>
            <CampaignListTable
              campaigns={visibleCampaigns}
              onEdit={setEditingCampaign}
              onUsers={(campaign) => openUsers(campaign, "all")}
            />
            <CampaignListPagination
              currentPage={pageNumber}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={links.per_page || pageSize}
              canPrev={canPrev}
              canNext={canNext}
              isFetching={isFetching}
              onPrev={() => setCurrentPage((page) => Math.max(1, page - 1))}
              onNext={() => setCurrentPage((page) => page + 1)}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
